import React, { useState, useRef, useCallback } from 'react';
import { Users, ChevronDown, ChevronRight, Briefcase, Circle } from 'lucide-react';
import { Badge } from './ui/badge';

const LEVEL_COLORS = [
  { bg: 'bg-[#002FA7]', text: 'text-white', ring: 'ring-[#002FA7]/30', border: 'border-[#002FA7]' },
  { bg: 'bg-violet-500', text: 'text-white', ring: 'ring-violet-500/30', border: 'border-violet-400' },
  { bg: 'bg-amber-500', text: 'text-white', ring: 'ring-amber-500/30', border: 'border-amber-400' },
];
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);

export default function OrgChart({ nodes = [] }) {
  const [collapsed, setCollapsed] = useState(new Set());
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef(null);

  const toggle = (id) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Build parent->children map iteratively
  const childrenMap = {};
  for (const n of nodes) {
    if (n.parent_id) {
      if (!childrenMap[n.parent_id]) childrenMap[n.parent_id] = [];
      childrenMap[n.parent_id].push(n.id);
    }
  }
  const nodeMap = {};
  for (const n of nodes) nodeMap[n.id] = n;

  // Get visible nodes using BFS (iterative)
  const rootNodes = nodes.filter(n => !n.parent_id);

  const onMouseDown = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('[data-node]')) return;
    setDragging(true);
    setStartPos({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  }, [offset]);
  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    setOffset({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  }, [dragging, startPos]);
  const onMouseUp = useCallback(() => setDragging(false), []);
  const onWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(prev => Math.min(2, Math.max(0.3, prev - e.deltaY * 0.001)));
  }, []);

  // Render a subtree iteratively using a stack-based approach
  const renderSubtree = (rootId) => {
    const node = nodeMap[rootId];
    if (!node) return null;
    const children = childrenMap[rootId] || [];
    const isCollapsed = collapsed.has(rootId);
    const hasChildren = children.length > 0;
    const lc = LEVEL_COLORS[node.level] || LEVEL_COLORS[2];

    return (
      <div key={rootId} className="flex flex-col items-center" data-node>
        {/* Node card */}
        <div
          className={`relative bg-white border-2 ${lc.border} rounded-xl px-4 py-3 min-w-[160px] max-w-[200px] shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
          onClick={() => hasChildren && toggle(rootId)}
          data-testid={`org-node-${rootId}`}
        >
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full ${lc.bg} ${lc.text} flex items-center justify-center text-xs font-bold ring-2 ${lc.ring} shrink-0`}>
              {node.name ? node.name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-800 truncate">{node.name || 'Agent'}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <Badge className="text-[7px] px-1 py-0 h-3.5 bg-slate-50 text-slate-400 border-slate-200">L{node.level}</Badge>
                {node.agent_status === 'active' && <Circle size={6} fill="#10B981" className="text-emerald-500" />}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
            <span className="text-[9px] text-slate-400">{node.deal_count} deals</span>
            {node.override_earned > 0 && <span className="text-[9px] text-emerald-500 font-medium">{fmt(node.override_earned)}</span>}
          </div>
          {hasChildren && (
            <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center">
              {isCollapsed ? <ChevronRight size={10} className="text-slate-400" /> : <ChevronDown size={10} className="text-slate-400" />}
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && !isCollapsed && (
          <>
            {/* Vertical connector */}
            <div className="w-px h-6 bg-slate-200" />
            {/* Horizontal branch */}
            <div className="flex items-start gap-4">
              {children.length > 1 && (
                <div className="absolute" style={{ display: 'none' }} />
              )}
              {children.map((childId, idx) => (
                <div key={childId} className="flex flex-col items-center relative">
                  {children.length > 1 && (
                    <div className="w-px h-0 bg-slate-200" />
                  )}
                  {renderSubtree(childId)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (nodes.length === 0) {
    return (
      <div className="text-center py-12" data-testid="org-chart-empty">
        <Users size={40} className="text-slate-200 mx-auto mb-3" />
        <p className="text-sm text-slate-400">No network data to display.</p>
        <p className="text-xs text-slate-400">Recruit agents to see your org chart grow.</p>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="org-chart">
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
        <button onClick={() => setZoom(z => Math.min(2, z + 0.15))} className="w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-700 text-sm font-bold">+</button>
        <span className="text-[9px] text-slate-400 w-8 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.15))} className="w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-700 text-sm font-bold">−</button>
        <button onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }} className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 text-[9px]">⟳</button>
      </div>

      {/* Chart container with drag-to-explore */}
      <div
        ref={containerRef}
        className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50/50"
        style={{ height: '420px', cursor: dragging ? 'grabbing' : 'grab' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
      >
        <div
          className="inline-flex p-8 min-w-full min-h-full"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: dragging ? 'none' : 'transform 0.15s ease',
          }}
        >
          <div className="flex items-start gap-8">
            {rootNodes.map(root => renderSubtree(root.id))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-3">
        {LEVEL_COLORS.map((lc, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${lc.bg}`} />
            <span className="text-[9px] text-slate-400">Level {i}{i === 0 ? ' (You)' : i === 1 ? ' (Direct)' : ' (2nd Gen)'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
